
import { useState } from 'react';
import { Plus, Target, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PACTCard from '@/components/PACTCard';
import { PACT } from '@/types/pact';

interface PACTsListProps {
  pendingPACTs: PACT[];
  completedTodayPACTs: PACT[];
  activePacts: PACT[];
  today: string;
  getStreak: (pact: PACT) => number;
  handleToggleCompletion: (pactId: string) => void;
  handleToggleSkip: (pactId: string) => void;
  handlePACTCardClick: (pact: PACT) => void;
  handleShowBanner: (message: string, actionType: 'now-completed' | 'now-skipped' | 'now-missed') => void;
  onCreatePACT: () => void;
}

const PACTsList = ({
  pendingPACTs,
  completedTodayPACTs,
  activePacts,
  today,
  getStreak,
  handleToggleCompletion,
  handleToggleSkip,
  handlePACTCardClick,
  handleShowBanner,
  onCreatePACT
}: PACTsListProps) => {
  const [showCompletedSection, setShowCompletedSection] = useState(false);

  return (
    <>
      {/* Pending PACTs */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-deep-blue">
            {pendingPACTs.length > 0 ? "Pending Today" : "Active PACTs"}
          </h2>
          <Button
            onClick={onCreatePACT}
            className="bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-full w-10 h-10 p-0"
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>

        {activePacts.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-soft-blue rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-deep-blue/50" />
            </div>
            <h3 className="text-lg font-medium text-deep-blue mb-2">No PACTs yet</h3>
            <p className="text-deep-blue/70 mb-6">
              Create your first PACT and start showing up!
            </p>
            <Button
              onClick={onCreatePACT}
              className="bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-lg px-8"
            >
              Create Your First PACT
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingPACTs.map((pact) => (
              <PACTCard
                key={pact.id}
                pact={pact}
                streak={getStreak(pact)}
                isCompleted={pact.completedDays.includes(today)}
                onToggle={() => handleToggleCompletion(pact.id)}
                onSkip={() => handleToggleSkip(pact.id)}
                onCardClick={() => handlePACTCardClick(pact)}
                onShowBanner={handleShowBanner}
              />
            ))}
          </div>
        )}
      </div>

      {/* Completed Today Section */}
      {completedTodayPACTs.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => setShowCompletedSection(!showCompletedSection)}
            className="flex items-center justify-between w-full mb-4 text-left"
          >
            <h2 className="text-xl font-semibold text-deep-blue">
              Completed Today ({completedTodayPACTs.length})
            </h2>
            {showCompletedSection ? (
              <ChevronUp className="w-5 h-5 text-deep-blue" />
            ) : (
              <ChevronDown className="w-5 h-5 text-deep-blue" />
            )}
          </button>

          {showCompletedSection && (
            <div className="space-y-4">
              {completedTodayPACTs.map((pact) => (
                <PACTCard
                  key={pact.id}
                  pact={pact}
                  streak={getStreak(pact)}
                  isCompleted={pact.completedDays.includes(today)}
                  onToggle={() => handleToggleCompletion(pact.id)}
                  onSkip={() => handleToggleSkip(pact.id)}
                  onCardClick={() => handlePACTCardClick(pact)}
                  onShowBanner={handleShowBanner}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PACTsList;
